import React, { Component, ComponentType } from 'react'
import { Box, BoxProps, IconButton, IconButtonProps, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { mergeObjects } from '../utils'
import { GlobalModalState } from '../api-context'
import { ITableBaseContext, MapTableBaseContext } from './context'

export interface IActionRowProps<T> {
  value: T
  formEdit?: (value: T, tableContext: ITableBaseContext) => React.ReactNode
  formDelete?: (value: T, tableContext: ITableBaseContext) => React.ReactNode
  after?: (value: T, tableContext: ITableBaseContext) => React.ReactNode
  before?: (value: T, tableContext: ITableBaseContext) => React.ReactNode
  wrapProps?: BoxProps
  buttonEditProps?: IconButtonProps | ((value: T) => IconButtonProps)
  buttonDeleteProps?: IconButtonProps | ((value: T) => IconButtonProps)
  buttonEditConfig?: GlobalModalState
  buttonDeleteConfig?: GlobalModalState
}

function CreateActionRow<T>(params?: Omit<IActionRowProps<T>, 'value'>): ComponentType<IActionRowProps<T>> {
  class ActionRow extends Component<IActionRowProps<T>> {
    get propsMerge() {
      return mergeObjects<IActionRowProps<T>>(params, this.props)
    }

    getButtons = function* (that: ActionRow, thatProps: Omit<IActionRowProps<T>, 'value'>, tableContext: ITableBaseContext) {
      const beforeFunc = that.props?.before ?? thatProps?.before
      if (beforeFunc) {
        yield <React.Fragment key='Before'>{beforeFunc(that.props.value, tableContext)}</React.Fragment>
      }
      const editFunc = that.props?.formEdit ?? thatProps?.formEdit
      const editContent = editFunc ? editFunc(that.props.value, tableContext) : undefined
      if (editFunc && !!editContent) {
        const { buttonEditProps } = thatProps
        const p = typeof buttonEditProps === 'function' ? buttonEditProps(that.props.value) : buttonEditProps
        yield (
          <Tooltip title='Edit' arrow key='Edit'>
            <span>
              <IconButton
                color='primary'
                {...p}
                onClick={(e) => {
                  tableContext?.showModal({
                    renderContent: () => editContent || <React.Fragment />,
                    sx: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
                    backdropActivated: true,
                    ...params?.buttonEditConfig
                  })
                  p?.onClick?.(e)
                }}
              >
                <EditIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        )
      }
      const deleteFunc = that.props?.formDelete ?? thatProps?.formDelete
      const deleteContent = deleteFunc ? deleteFunc(that.props.value, tableContext) : undefined
      if (deleteFunc && !!deleteContent) {
        const { buttonDeleteProps } = thatProps
        const p = typeof buttonDeleteProps === 'function' ? buttonDeleteProps(that.props.value) : buttonDeleteProps
        yield (
          <Tooltip title='Delete' arrow key='Delete'>
            <span>
              <IconButton
                color='error'
                {...p}
                onClick={(e) => {
                  tableContext?.showModal({
                    renderContent: () => deleteContent || <React.Fragment />,
                    sx: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
                    backdropActivated: true,
                    ...params?.buttonDeleteConfig
                  })
                  p?.onClick?.(e)
                }}
              >
                <DeleteIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        )
      }
      const afterFunc = that.props?.after ?? thatProps?.after
      if (afterFunc) {
        yield <React.Fragment key='After'>{afterFunc(that.props.value, tableContext)}</React.Fragment>
      }
    }

    render() {
      return MapTableBaseContext((context) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }} {...this.propsMerge.wrapProps}>
          {Array.from(this.getButtons(this, this.propsMerge, context))}
        </Box>
      ))
    }
  }

  return ActionRow
}
export default CreateActionRow
